const IDB_NAME = 'export-polling-db';
const IDB_VERSION = 1;
const IDB_STORE = 'export-queue';

function openIDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE, { keyPath: 'downloadId' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function idbPut(db, item) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).put(item);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

function idbGet(db, key) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function idbGetAll(db) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).getAll();
        req.onsuccess = () => resolve(req.result ?? []);
        req.onerror = () => reject(req.error);
    });
}

function idbDelete(db, key) {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        tx.objectStore(IDB_STORE).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export function createExportPollingStorage() {
    let db = null;
    let useMemoryFallback = false;
    const memoryStore = new Map();

    const ready = openIDB()
        .then(result => {
            db = result;
        })
        .catch(error => {
            useMemoryFallback = true;
            console.warn('[ExportQueue] IndexedDB unavailable, using memory fallback', error);
        });

    async function getItem(key) {
        await ready;
        if (useMemoryFallback) {
            const item = memoryStore.get(key);
            return item ? { ...item } : undefined;
        }
        return idbGet(db, key);
    }

    async function getAllItems() {
        await ready;
        if (useMemoryFallback) {
            return Array.from(memoryStore.values()).map(item => ({ ...item }));
        }
        return idbGetAll(db);
    }

    async function putItem(item) {
        await ready;
        if (useMemoryFallback) {
            memoryStore.set(item.downloadId, { ...item });
            return;
        }
        await idbPut(db, item);
    }

    async function patchItem(downloadId, changes) {
        await ready;
        if (useMemoryFallback) {
            const item = memoryStore.get(downloadId);
            if (item) memoryStore.set(downloadId, { ...item, ...changes });
            return;
        }
        const item = await idbGet(db, downloadId);
        if (item) await idbPut(db, { ...item, ...changes });
    }

    async function deleteItem(downloadId) {
        await ready;
        if (useMemoryFallback) {
            memoryStore.delete(downloadId);
            return;
        }
        await idbDelete(db, downloadId);
    }

    return {
        ready,
        getItem,
        getAllItems,
        putItem,
        patchItem,
        deleteItem,
    };
}
